<script setup lang="ts">

import OpenInNewTabIcon from '@/components/OpensInNewTabIcon.vue'
import LibrarySaveChangesButton from '@/components/LibrarySaveChangesButton.vue'
import FolderAvatar from '@/library/FolderAvatar.vue'
import { computed, PropType, ref } from 'vue'
import { timeAgo } from '@/util/time'
import { useLibraryStore } from '@/library/library.store.ts'
import { SavingState } from '@/presentlevels/presentlevels_types.ts'
import router from '@/router'
import { useConfirm } from 'primevue/useconfirm'
import { storeToRefs } from 'pinia'
import { CONTENT_TYPES } from '@/util/access_visibility'
import { LibraryContentObject } from '@/library/library_types.ts'
import CopySummaryModal from '@/presentlevels/CopySummaryModal.vue'
import CopyImpactOfDisabilityModal from '@/impact-of-disability/CopyImpactOfDisabilityModal.vue'
import TextLinkButton from '@/components/TextLinkButton.vue'
import FolderIcon from '@/library/FolderIcon.vue'
import { betaOptIn } from '@/util/beta_opt_in'
import CopyGoalForAnotherStudentButton from '@/library/CopyGoalForAnotherStudentButton.vue'
import type { CopyGoalProps } from '@/library/library_types'

interface ContentLabel {
  removal: string
  removalHeading: string
  copyable: boolean
  printable?: boolean
  originalPageRoute?: (sourceContentUuid: string) => { name: string, params: Record<string, string> }
}

const CONTENT_LABELS: Record<string, ContentLabel> = {
  [CONTENT_TYPES.PRESENTLEVEL_STUDENT_SUMMARY]: {
    removal: 'present levels statement',
    removalHeading: 'Present Levels Statement',
    copyable: true
  },
  [CONTENT_TYPES.SAVED_IMPACT_OF_DISABILITY]: {
    removal: 'Impact of Disability statement',
    removalHeading: 'Impact of Disability Statement',
    copyable: true
  },
  [CONTENT_TYPES.SAVED_SUPER_PAGE]: {
    removal: 'goal',
    removalHeading: 'Goal',
    copyable: false,
    printable: true,
    originalPageRoute: (uuid) => ({ name: 'AnchorPage', params: { uuid } })
  },
  [CONTENT_TYPES.SAVED_STRATEGY]: {
    removal: 'strategy',
    removalHeading: 'Strategy',
    copyable: false,
    originalPageRoute: (slugOrUuid) => ({ name: 'StrategyDetailPage', params: { slugOrUuid } })
  },
  [CONTENT_TYPES.PRESENTLEVEL]: {
    removal: 'present levels statement',
    removalHeading: 'Present Levels Statement',
    copyable: false
  }
}
const UNKNOWN_CONTENT_LABEL: ContentLabel = { removal: 'content', removalHeading: 'Content', copyable: false }
const AUTOSAVED_CONTENT_TYPES: string[] = [CONTENT_TYPES.PRESENTLEVEL]

const props = defineProps({
  contentUuid: {
    type: String,
    required: true
  },
  saveStatus: {
    type: String as PropType<SavingState>,
    required: true
  },
  updatedAt: {
    type: String,
  },
  contentType: {
    type: String,
    required: true
  },
  sourceContentUuid: {
    type: String
  },
  isSaveButtonHidden: {
    type: Boolean,
    default: false
  },
  copyGoal: {
    type: Object as PropType<CopyGoalProps>,
    default: undefined
  }
})

const libraryStore = useLibraryStore()
const confirm = useConfirm()

const emit = defineEmits<{
  save: []
  discard: []
  print: []
  remove: []
  removeConfirmed: []
}>()

const { currentFolder } = storeToRefs(libraryStore)
const openCopySummaryModal = ref(false)
const isRemoving = ref(false)

const formattedLastUpdated = computed(() => {
  return props.updatedAt ? timeAgo(props.updatedAt) : ''
})

const currentContent = computed(() => {
  let contentList: LibraryContentObject[] = []
  switch (props.contentType) {
    case CONTENT_TYPES.PRESENTLEVEL_STUDENT_SUMMARY:
      contentList = libraryStore.presentlevelStudentSummaries
      break;
    case CONTENT_TYPES.SAVED_IMPACT_OF_DISABILITY:
      contentList = libraryStore.impactsOfDisabilities
      break;
    case CONTENT_TYPES.SAVED_STRATEGY:
      contentList = libraryStore.strategies
      break;
    case CONTENT_TYPES.SAVED_SUPER_PAGE:
      contentList = libraryStore.goals
      break;
    case CONTENT_TYPES.PRESENTLEVEL:
      contentList = libraryStore.presentlevels
      break;
    default:
      console.warn(`unsupported content type:${props.contentType}`)
  }
  return contentList.find(c => c.content_uuid === props.contentUuid)
})

const contributorUUID = computed(() => {
  if (!currentContent.value) {
    return null
  }
  return currentContent.value.contributor
})

const contentOwnerUuid = computed(() => {
  if (libraryStore.currentFolderOwner) {
    return libraryStore.currentFolderOwner
  }
  return contributorUUID.value
})

const shouldShowContributor = computed(() => {
  if (!contributorUUID.value || !libraryStore.currentFolderOwner) {
    return false
  }
  return contentOwnerUuid.value !== contributorUUID.value
})

const folderDisplayName = computed(() => {
  const maxTitleLength = 30
  if (!currentFolder.value) {
    return ''
  }
  const name = currentFolder.value.name
  if (name.length < maxTitleLength) {
    return name
  }
  return `${name.slice(0,maxTitleLength)}...`
})

const contentLabel = computed(() => CONTENT_LABELS[props.contentType] ?? UNKNOWN_CONTENT_LABEL)

const isAutosavedContent = computed(() => AUTOSAVED_CONTENT_TYPES.includes(props.contentType))

// Vue 2 hid the copy until the page knew which goal it was copying and whether it was personalized.
const isCopyGoalShown = computed(() =>
  props.contentType === CONTENT_TYPES.SAVED_SUPER_PAGE &&
  Boolean(props.copyGoal?.sourceGoalUuid) &&
  props.copyGoal?.isPersonalized !== undefined
)

const originalPageLink = computed(() => {
  const toOriginalPage = contentLabel.value.originalPageRoute
  if (!toOriginalPage || !props.sourceContentUuid) {
    return null
  }
  return { ...toOriginalPage(props.sourceContentUuid), query: betaOptIn(router.currentRoute.value.query) }
})

const removeFromFolder = () => {
  if (!currentFolder.value?.uuid || isRemoving.value) {
    return
  }
  
  confirm.require({
    group: `${props.contentUuid}_${currentFolder.value.uuid}`,
    message: `Are you sure you want to delete this ${contentLabel.value.removal} from this folder? This action is irreversible.`,
    header: `Delete ${contentLabel.value.removalHeading}`,
    accept: async () => {
      emit('removeConfirmed')
      if (isAutosavedContent.value) {
        await removeAndStayOnPage()
        return
      }
      await removeAndOpenFolder()
    }
  })
}

const deleteFromCurrentFolder = async () => {
  await libraryStore.deleteResource({
    uuid: props.contentUuid,
    type: props.contentType,
    collection: currentFolder.value.uuid
  })
  emit('remove')
}

const removeAndOpenFolder = async () => {
  await deleteFromCurrentFolder()
  router.push({
    name: 'LibraryFolder',
    params: { uuid: currentFolder.value?.uuid },
    query: betaOptIn(router.currentRoute.value.query)
  })
}

const removeAndStayOnPage = async () => {
  isRemoving.value = true
  try {
    await deleteFromCurrentFolder()
  } finally {
    isRemoving.value = false
  }
}
</script>

<template>
  <div class="bg-green-100 rounded-sm border border-gray-400 p-4">
    <div class="flex justify-between items-center mb-2">
      <div class="flex items-center font-bold text-green-300 gap-1">
        <FolderIcon :is-shared="libraryStore.isCurrentFolderShared" color-class="text-green-300" size-class="text-xl" />
        <router-link
          v-if="currentFolder.uuid"
          :to="{ name: 'LibraryFolder', params: { uuid: currentFolder.uuid } }"
          class="text-xl"
          target="_blank"
          rel="noopener"
          :aria-label="`Open ${currentFolder.name} folder in a new tab`"
        >
          <span data-testid="folder-name" class="mr-1 hover:underline fs-exclude">{{ folderDisplayName }}</span>
          <OpenInNewTabIcon />
        </router-link>
      </div>
    </div>

    <div class="flex items-center text-gray-900 mb-2">
      <span class="material-icons-round mr-1 text-text-md" aria-hidden="true">calendar_month</span>
      <span class="text-text-sm">Saved {{ formattedLastUpdated }}</span>
    </div>

    <div v-if="libraryStore.isCurrentFolderShared" class="flex items-center gap-1 mb-4 text-sm">
      <template v-if="libraryStore.currentFolderOwner">
        <span class="text-gray-900">Owner:</span>
        <FolderAvatar :user-id="libraryStore.currentFolderOwner" :folder="currentFolder" avatar-size="sm" />
      </template>
      <template v-if="shouldShowContributor">
        <span class="text-gray-900 ml-3">Contributed by:</span>
        <FolderAvatar :user-id="contributorUUID || ''" :folder="currentFolder" avatar-size="sm" />
      </template>
    </div>

    <template v-if="!isSaveButtonHidden">
      <LibrarySaveChangesButton
        v-if="libraryStore.doesUserOwnCurrentFolder"
        :save-status="saveStatus"
        :is-autosaved="isAutosavedContent"
        :is-removing="isRemoving"
        @save="$emit('save')"
        @discard="$emit('discard')"
      />
      <div v-else class="flex items-center p-1 bg-white border rounded-sm mb-4">
        <span class="material-icons text-gray-900 text-sm mr-1" aria-hidden="true">lock</span>
        <span class="text-sm text-gray-900 italic">Only the owner of this Shared Folder can make edits.</span>
      </div>
    </template>

    <div class="mt-4 flex flex-col gap-2">
      <CopyGoalForAnotherStudentButton
        v-if="isCopyGoalShown"
        v-bind="copyGoal"
      />

      <TextLinkButton
        v-if="contentLabel.printable"
        text="Print this goal"
        icon-name="print"
        @click="emit('print')"
      />

      <TextLinkButton
        v-if="contentLabel.copyable"
        text="Copy statement to another folder"
        icon-name="content_copy"
        @click="openCopySummaryModal = true"
      />

      <router-link
        v-if="originalPageLink"
        :to="originalPageLink"
        class="flex w-fit items-center text-blue-400 hover:underline"
      >
        <span class="material-icons-round text-text-md mr-0.5 font-bold" aria-hidden="true">shortcut</span>
        <span class="text-text-md">View original page</span>
      </router-link>

      <TextLinkButton
        v-if="libraryStore.canDeleteCurrentFolderContent"
        color="red"
        text="Remove from folder"
        icon-name="delete"
        @click="removeFromFolder"
      />
    </div>

  </div>
  <CopySummaryModal
    v-if="props.contentType === CONTENT_TYPES.PRESENTLEVEL_STUDENT_SUMMARY"
    :visible="openCopySummaryModal"
    :content-uuid="props.contentUuid"
    @update:visible="(val: boolean) => { openCopySummaryModal = val }"
  />
  <CopyImpactOfDisabilityModal
    v-if="props.contentType === CONTENT_TYPES.SAVED_IMPACT_OF_DISABILITY"
    :visible="openCopySummaryModal"
    :content-uuid="props.contentUuid"
    @update:visible="(val: boolean) => { openCopySummaryModal = val }"
  />
</template>

<style scoped>

</style>